import { useEffect, useState } from 'react';
import { useNavigation, useRevalidator } from 'react-router-dom';

/** @typedef {import('./combinedHistory').CombinedHistory} CombinedHistory */

/**
 * @param {unknown} error A rejection of the history request.
 * @returns {boolean} Whether it is the 401 of an expired session.
 */
const isUnauthorized = error =>
  error instanceof Response && error.status === 401;

/**
 * Awaits the history request a submission history page's loader returns,
 * tracks its reload, and starts one from the Refresh button.
 *
 * The page reads as loading until the request settles. Refresh revalidates
 * the route in place, which `useNavigation` never reports as loading, so the
 * revalidator's state is read too. While the same claimant's history
 * reloads, the last history stays, so the search box and the toolbar keep
 * their place. Another claimant's page starts with none.
 *
 * A 401 rejection reads as loading, since `utilities/api.js` has already
 * sent the browser to sign-in. Any other rejection is thrown while
 * rendering, for the route's error boundary.
 *
 * A request nothing will read any more is cancelled, whether a reader left
 * the page during it or a newer load replaced it. The router aborts a
 * loader's own signal only while the loader runs, and these loaders return
 * before their request settles.
 *
 * @param {Promise<CombinedHistory>|undefined} request The loader's
 *   `combinedHistory`, or undefined when the loader gave none, as with the
 *   toggle off.
 * @param {string|null} [claimantId] The claimant the history is limited to,
 *   or null for the representative's own.
 * @param {() => void} [abort] The loader's `abortCombinedHistory`, which
 *   cancels `request`.
 * @returns {{ history: CombinedHistory|undefined, isLoading: boolean, onRefresh: () => void }}
 *   The last history settled for this claimant, whether the route's loader
 *   or the history request is running, and a function that runs the loader
 *   again without changing the URL.
 * @throws {unknown} The request's rejection, other than a 401.
 * @example
 * const { history, isLoading, onRefresh } = useHistoryReload(
 *   loaderData.combinedHistory,
 *   null,
 *   loaderData.abortCombinedHistory,
 * );
 */
export const useHistoryReload = (request, claimantId = null, abort) => {
  const navigation = useNavigation();
  const revalidator = useRevalidator();
  const [settled, setSettled] = useState(null);

  useEffect(() => {
    if (!request) return undefined;
    let isCurrent = true;
    request.then(
      history => {
        if (isCurrent) setSettled({ request, claimantId, history });
      },
      error => {
        if (isCurrent && !isUnauthorized(error)) {
          setSettled({ request, claimantId, error });
        }
      },
    );
    return () => {
      isCurrent = false;
      // A no-op once the request has settled.
      abort?.();
    };
  }, [request, claimantId, abort]);

  if (settled?.error && settled.request === request) throw settled.error;

  return {
    history:
      settled?.claimantId === claimantId && !settled.error
        ? settled.history
        : undefined,
    isLoading:
      navigation.state === 'loading' ||
      revalidator.state === 'loading' ||
      Boolean(request && settled?.request !== request),
    onRefresh: revalidator.revalidate,
  };
};
